/**
 * InsightChartCard
 * 
 * Visual chart card for displaying insights (like sales trends) in the agent chat.
 * Uses SVG for lightweight, interactive sparklines and trends.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InsightAction, InsightChartCardData } from '../types';

interface InsightChartCardProps {
  data: InsightChartCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

function buildLinePath(values: number[], width: number, height: number) {
  if (values.length === 0) return '';
  const max = Math.max(...values, 1);
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export function InsightChartCard({ data, onSelect }: InsightChartCardProps) {
  const navigate = useNavigate();
  const values = useMemo(() => data.points.map((point) => point.value ?? 0), [data.points]);
  const maxValue = Math.max(...values, 1);
  const path = useMemo(() => buildLinePath(values, 280, 100), [values]);

  const handleAction = (action: InsightAction) => {
    if (action.destination) {
      navigate(action.destination);
    }
    onSelect({ action_id: action.action_id, destination: action.destination });
  };

  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Trend</p>
        <h3 className="mt-1 text-sm font-semibold text-slate-900">{data.title}</h3>
        {data.subtitle && <p className="mt-1 text-xs text-slate-500">{data.subtitle}</p>}
      </div>

      {(data.metrics?.length ?? 0) > 0 && (
        <div className="grid gap-2 border-b border-slate-100 px-4 py-3 sm:grid-cols-3">
          {data.metrics?.map((metric) => (
            <div key={metric.id} className="rounded-xl bg-slate-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">{metric.label}</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{metric.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-4">
        {data.chart_type === 'bar' ? (
          <div className="flex h-32 items-end gap-2">
            {data.points.map((point, idx) => (
              <div key={`${point.label}-${idx}`} className="flex flex-1 flex-col items-center gap-2">
                <div className="flex h-24 w-full items-end">
                  <div
                    className="w-full rounded-t-xl bg-gradient-to-t from-primary-600 to-sky-400"
                    style={{ height: `${Math.max(((point.value ?? 0) / maxValue) * 100, 6)}%` }}
                  />
                </div>
                <span className="text-[10px] text-slate-500">{point.label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <svg viewBox="0 0 280 100" className="h-32 w-full overflow-visible">
              <path d={path} fill="none" stroke="url(#trend)" strokeWidth="3" strokeLinecap="round" />
              <defs>
                <linearGradient id="trend" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0284c7" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
              </defs>
              {data.points.map((point, index) => {
                const x = data.points.length === 1 ? 140 : (index / (data.points.length - 1)) * 280;
                const y = 100 - ((point.value ?? 0) / maxValue) * 100;
                return <circle key={`${point.label}-${index}`} cx={x} cy={y} r="4" fill="#0f172a" />;
              })}
            </svg>
            <div className="mt-2 grid grid-cols-4 gap-2 text-[10px] text-slate-500">
              {data.points.slice(0, 4).map((point, idx) => (
                <div key={`${point.label}-${idx}`} className="rounded-lg bg-slate-50 px-2 py-1 text-center">
                  <div>{point.label}</div>
                  <div className="font-medium text-slate-700">{(point.value ?? 0).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-slate-100 px-3 py-3">
        {(data.actions ?? []).map((action) => (
          <button
            key={action.action_id}
            type="button"
            onClick={() => handleAction(action)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
              action.variant === 'secondary'
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {action.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSelect({ action_id: 'dismiss' })}
          className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
